import React, { memo } from 'react';
import { DatePicker, Drawer, Button, Table, Input, InputNumber, Select } from 'antd';
import { Store } from 'lucide-react';
import dayjs from 'dayjs';

import StoreSelectionModal from './StoreSelectionModal';
import { FormField } from '../../common';
import { useContractForm } from './contractElements.controller';
import { CONTRACT_TYPES } from '../../../utils/contractTypes';
import { formatCurrency } from '../../../utils/format';

const contractTypes = CONTRACT_TYPES.map(({ label, value }) => ({ label, value }));

const StoreColumns = [
    {
        title: 'Nombre',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <span className="text-[13px] text-surface-900">{text}</span>
    },
    {
        title: 'Cliente',
        dataIndex: 'clientName',
        key: 'clientName',
        render: (text) => <span className="text-[13px] text-surface-600">{text}</span>
    },
    {
        title: 'Tipo de PDV',
        dataIndex: 'storeType',
        key: 'storeType',
        render: (text) => <span className="text-[13px] text-surface-600">{text}</span>
    }
];

const ContractDrawer = memo(({ isOpen, setIsOpen, createContract, updateContract, contextHolder, stores, editMode = false, contractData = null }) => {
    const form = useContractForm({ isOpen, setIsOpen, createContract, updateContract, stores, editMode, contractData });

    return (
        <Drawer
            title={editMode ? 'Editar Contratación' : 'Crear Contratación'}
            onClose={form.onCloseDrawer}
            open={isOpen}
            width={600}
            footer={
                <div className="flex justify-end gap-2">
                    <Button onClick={form.onCloseDrawer}>Cancelar</Button>
                    <Button type="primary" onClick={form.onSubmit} disabled={form.loading} loading={form.loading}>
                        {editMode ? 'Actualizar Contratación' : 'Crear Contratación'}
                    </Button>
                </div>
            }>
            {contextHolder}
            <div className="flex flex-col gap-5">
                <FormField label="Nombre de la contratación" required>
                    <Input value={form.name} onChange={(e) => form.setName(e.target.value)} allowClear />
                </FormField>

                <div className="grid gap-5 sm:grid-cols-2">
                    <FormField label="Seleccione el tipo de contratación" required>
                        <Select value={form.contractType} options={contractTypes} onChange={(e) => form.setContractType(e)} />
                    </FormField>
                    <FormField label="Seleccione la fecha de vencimiento" required>
                        <DatePicker
                            minDate={form.today}
                            value={form.expirationDate ? dayjs(form.expirationDate) : null}
                            onChange={(e) => form.setExpirationDate(e ? e.format('YYYY-MM-DD') : '')}
                            className="w-full"
                        />
                    </FormField>
                </div>

                <FormField
                    label="Precio por piso de venta (USD)"
                    hint="Se aplica a cada piso de venta. Podrás ajustar el precio de un piso puntual desde el detalle de la contratación.">
                    <InputNumber
                        value={form.price}
                        onChange={(value) => form.setPrice(value ?? 0)}
                        min={0}
                        step={1}
                        precision={2}
                        prefix="$"
                        className="w-full"
                    />
                </FormField>

                <div className="flex flex-col gap-3 pt-5 border-t border-surface-100">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-surface-900">Pisos de venta</div>
                            <div className="text-xs text-surface-500 tabular-nums">{form.selectedStores.length} seleccionados</div>
                        </div>
                        <Button icon={<Store />} onClick={() => form.setIsStoreModalOpen(true)}>
                            Seleccionar Pisos de Venta
                        </Button>
                    </div>

                    {form.selectedStores.length > 0 && (
                        <>
                            <div className="rounded-lg border border-surface-200 overflow-hidden">
                                <Table
                                    dataSource={form.filteredStores}
                                    columns={StoreColumns}
                                    rowKey="_id"
                                    size="small"
                                    scroll={{ y: 300 }}
                                    pagination={false}
                                />
                            </div>
                            <div className="flex items-center justify-between rounded-lg border border-highlight-border bg-highlight-bg px-4 py-3">
                                <span className="text-[13px] text-surface-600">Inversión estimada ({form.selectedStores.length} pisos)</span>
                                <span className="text-sm font-semibold text-surface-900 tabular-nums">{formatCurrency(form.estimatedInvestment)}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <StoreSelectionModal
                isOpen={form.isStoreModalOpen}
                setIsOpen={form.setIsStoreModalOpen}
                setSelectedStores={form.setSelectedStores}
                selectedStores={form.selectedStores}
                stores={stores}
            />
        </Drawer>
    );
});

export default ContractDrawer;
